import {Evaluator} from "@cm/graph-v3"
import {registerImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/register-image-op-node"
import {
    AnyImageOpContext,
    AnyImageOpResultNode,
    AnyImageOpResultOrNode,
    ImageOpOutputs,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/composite/normalized-cross-correlation/definition"
import {ImageOpNodeMath} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/math/node"
import {ImageOpNodeReduce} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/reduce/node"
import {ImageOpNodeExtractRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/node"
import {ImageOpNodeExtractPatches} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-patches/node"
import {ImageOpNodeReshape} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/reshape/node"
import {ImageOpNodeConvertToGrayscale} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/convert-to-grayscale/node"
import {CompositeImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/composite-image-op-node"

const nodeDefinition = {...definition, nodeClass: "ImageOpNodeNormalizedCrossCorrelation"}

@registerImageOpNode
export class ImageOpNodeNormalizedCrossCorrelation extends CompositeImageOpNode<typeof definition> {
    override get definition() {
        return nodeDefinition
    }

    override async execute(evaluator: Evaluator<AnyImageOpContext>, imageOpNodeBag: ImageOpNodeBag): Promise<ImageOpOutputs<typeof definition>> {
        const {
            imageOpHalOptions,
            sourceImage: sourceImageIn,
            sourceWeightImage: sourceWeightImageIn,
            sourceRegion: sourceRegionIn,
            templateImage: templateImageIn,
            templateWeightImage: templateWeightImageImageIn,
            templateRegion: templateRegionIn,
            stride,
            options,
        } = await evaluator.getAll(this.parameters)
        const premultipliedImages = options?.premultipliedImages ?? false

        // some helper functions
        const makeMeanFree = (prefix: string, data: AnyImageOpResultOrNode, weights?: AnyImageOpResultOrNode): AnyImageOpResultOrNode => {
            let dataMean: AnyImageOpResultOrNode
            if (weights) {
                const weightSum = imageOpNodeBag.getNode(prefix + "WeightSum", ImageOpNodeReduce, {
                    imageOpHalOptions,
                    sourceImage: weights,
                    operator: "sum",
                })
                const dataTimesWeight = imageOpNodeBag.getNode(prefix + "DataTimesWeight", ImageOpNodeMath, {
                    imageOpHalOptions,
                    operator: "*",
                    operandA: data,
                    operandB: weights,
                })
                const dataTimesWeightSum = imageOpNodeBag.getNode(prefix + "DataTimesWeightSum", ImageOpNodeReduce, {
                    imageOpHalOptions,
                    sourceImage: dataTimesWeight,
                    operator: "sum",
                })
                dataMean = imageOpNodeBag.getNode(prefix + "Mean", ImageOpNodeMath, {
                    imageOpHalOptions,
                    operator: "/safe",
                    operandA: dataTimesWeightSum,
                    operandB: weightSum,
                })
            } else {
                dataMean = imageOpNodeBag.getNode(prefix + "Mean", ImageOpNodeReduce, {
                    imageOpHalOptions,
                    sourceImage: data,
                    operator: "mean",
                })
            }
            return imageOpNodeBag.getNode(prefix + "SubMean", ImageOpNodeMath, {
                imageOpHalOptions,
                operator: "-",
                operandA: data,
                operandB: dataMean,
            })
        }
        const factorInWeightsAndReduce = (prefix: string, data: AnyImageOpResultOrNode, weights?: AnyImageOpResultOrNode): AnyImageOpResultOrNode => {
            // factor in weights if they exist
            if (weights) {
                data = imageOpNodeBag.getNode(prefix + "MulWeights", ImageOpNodeMath, {
                    imageOpHalOptions,
                    operator: "*",
                    operandA: data,
                    operandB: weights,
                })
            }
            // sum
            return imageOpNodeBag.getNode(prefix + "Reduce", ImageOpNodeReduce, {
                imageOpHalOptions,
                sourceImage: data,
                operator: "sum",
            })
        }

        let templateImage: AnyImageOpResultOrNode = templateImageIn
        let templateWeightImage: AnyImageOpResultOrNode | undefined = templateWeightImageImageIn
        if (templateRegionIn) {
            templateImage = imageOpNodeBag.getNode("extractTemplateRegion", ImageOpNodeExtractRegion, {
                imageOpHalOptions,
                sourceImage: templateImage,
                region: templateRegionIn,
                addressMode: "border",
            })
            if (templateWeightImage) {
                templateWeightImage = imageOpNodeBag.getNode("extractTemplateWeightRegion", ImageOpNodeExtractRegion, {
                    imageOpHalOptions,
                    sourceImage: templateWeightImage,
                    region: templateRegionIn,
                    addressMode: "border",
                })
            }
        }

        const templateRegion =
            templateRegionIn ??
            imageOpNodeBag.getLambda("getTemplateRegion", {templateImage}, ({templateImage}) => ({
                x: 0,
                y: 0,
                width: templateImage.descriptor.width,
                height: templateImage.descriptor.height,
            }))
        const sourceRegion =
            sourceRegionIn ??
            imageOpNodeBag.getLambda("getSourceRegion", {sourceImage: sourceImageIn}, ({sourceImage}) => ({
                x: 0,
                y: 0,
                width: sourceImage.descriptor.width,
                height: sourceImage.descriptor.height,
            }))

        let sourceImage: AnyImageOpResultOrNode = sourceImageIn
        const sourceWeightImage: AnyImageOpResultOrNode | undefined = sourceWeightImageIn

        // TODO can we avoid this conversion ?
        if (premultipliedImages) {
            // remove pre-multiplication
            if (sourceWeightImage) {
                sourceImage = imageOpNodeBag.getNode("RemoveSourcePreMultiplication", ImageOpNodeMath, {
                    imageOpHalOptions,
                    operator: "/safe",
                    operandA: sourceImage,
                    operandB: sourceWeightImage,
                })
            }
            if (templateWeightImage) {
                templateImage = imageOpNodeBag.getNode("RemoveTemplatePreMultiplication", ImageOpNodeMath, {
                    imageOpHalOptions,
                    operator: "/safe",
                    operandA: templateImage,
                    operandB: templateWeightImage,
                })
            }
        }

        // debugImage?.addImage(templateImage)
        // if (templateWeightImage) {
        //     debugImage?.addImage(templateWeightImage)
        // }

        // extract the patches of the source image
        let sourcePatches: AnyImageOpResultOrNode = imageOpNodeBag.getNode("ExtractSourcePatches", ImageOpNodeExtractPatches, {
            imageOpHalOptions,
            sourceImage,
            sourceRegion,
            patchSize: imageOpNodeBag.getLambda("getPatchSize", {templateRegion}, ({templateRegion}) => ({
                width: templateRegion.width,
                height: templateRegion.height,
            })),
            stride,
            addressMode: "border",
        })
        const sourceWeightPatches: AnyImageOpResultOrNode | undefined = sourceWeightImage
            ? imageOpNodeBag.getNode("ExtractSourceWeightPatches", ImageOpNodeExtractPatches, {
                  imageOpHalOptions,
                  sourceImage: sourceWeightImage,
                  sourceRegion,
                  patchSize: templateRegion,
                  stride,
                  addressMode: "border",
              })
            : undefined

        // debugImage?.addImage(sourcePatches)
        // if (sourceWeightPatches) {
        //     debugImage?.addImage(sourceWeightPatches)
        // }

        // compute the weight product
        const weightProduct =
            sourceWeightPatches && templateWeightImage
                ? imageOpNodeBag.getNode("ComputeWeightProduct", ImageOpNodeMath, {
                      imageOpHalOptions,
                      operator: "*",
                      operandA: sourceWeightPatches,
                      operandB: templateWeightImage,
                  })
                : sourceWeightPatches
                  ? sourceWeightPatches
                  : templateWeightImage
                    ? templateWeightImage
                    : undefined

        // if (weightProduct) {
        //     debugImage?.addImage(weightProduct)
        // }

        // remove mean from template
        templateImage = makeMeanFree("Template", templateImage, weightProduct)
        // debugImage?.addImage(templateImage, {scale: 0.5, offset: 0.5})

        // remove mean from source patches
        sourcePatches = makeMeanFree("Source", sourcePatches, weightProduct) // TODO we're computing the weightProductSum a 2nd time here
        // debugImage?.addImage(sourcePatches, {scale: 0.5, offset: 0.5})

        // compute the numerator of the NCC
        const sourceTemplateProduct = imageOpNodeBag.getNode("ComputeSourceTemplateProduct", ImageOpNodeMath, {
            imageOpHalOptions,
            operator: "*",
            operandA: sourcePatches,
            operandB: templateImage,
        })
        const numerator = factorInWeightsAndReduce("Numerator", sourceTemplateProduct, weightProduct)

        // const scale = 500 /// templateRegion.width * templateRegion.height
        // debugImage?.addImage(numerator, {scale: scale, offset: 0.5})

        // compute the denominator of the NCC
        // source patch term
        const sourceSquared = imageOpNodeBag.getNode("SourceSquared", ImageOpNodeMath, {
            imageOpHalOptions,
            operator: "square",
            operand: sourcePatches,
        })
        const denominatorSourcePatchTerm = factorInWeightsAndReduce("DenominatorSourcePatchTerm", sourceSquared, weightProduct)
        // template term
        const templateSquared = imageOpNodeBag.getNode("TemplateSquared", ImageOpNodeMath, {
            imageOpHalOptions,
            operator: "square",
            operand: templateImage,
        })
        const denominatorTemplateTerm = factorInWeightsAndReduce("DenominatorTemplateTerm", templateSquared, weightProduct)
        // compute final denominator
        const denominatorTermProduct = imageOpNodeBag.getNode("DenominatorTermProduct", ImageOpNodeMath, {
            imageOpHalOptions,
            operator: "*",
            operandA: denominatorSourcePatchTerm,
            operandB: denominatorTemplateTerm,
        })
        const denominator = imageOpNodeBag.getNode("Denominator", ImageOpNodeMath, {
            imageOpHalOptions,
            operator: "sqrt",
            operand: denominatorTermProduct,
        })

        // debugImage?.addImage(denominator, {scale: scale})

        // compute final ncc with safe division
        let crossCorrelation: AnyImageOpResultNode = imageOpNodeBag.getNode("CrossCorrelation", ImageOpNodeMath, {
            imageOpHalOptions,
            operator: "/safe",
            operandA: numerator,
            operandB: denominator,
        })

        // debugImage?.addImage(crossCorrelation)

        // if the source is an RGB image, reduce the correlation by averaging the individual channels
        crossCorrelation = imageOpNodeBag.getNode("ToGrayscale", ImageOpNodeConvertToGrayscale, {
            imageOpHalOptions,
            sourceImage: crossCorrelation,
            mode: "luminance",
        }) // weight by perceived brightness

        // reshape the result to remove batch dimension
        crossCorrelation = imageOpNodeBag.getNode("RemoveBatch", ImageOpNodeReshape, {
            imageOpHalOptions,
            sourceImage: crossCorrelation,
            shape: {
                numPatches: {x: 1, y: 1},
            },
        })

        return await evaluator.get(crossCorrelation)
    }
}
